import { useModel as _useModel, mergeModels as _mergeModels, defineComponent as _defineComponent } from 'vue'
import { resolveComponent as _resolveComponent, createVNode as _createVNode, unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, renderSlot as _renderSlot, withCtx as _withCtx } from "vue"

const _hoisted_1 = { class: "LandingListFilters__wrapper column no-wrap bg-white shadow-2 rounded-16 q-pa-20 dynamic-zoom" }
const _hoisted_2 = { class: "row items-center justify-between no-wrap" }
const _hoisted_3 = { class: "row items-center gap-sm" }
const _hoisted_4 = { class: "fs-15px fw-500 text-cool-gray-100" }
const _hoisted_5 = { class: "LandingListFilters__filters column no-wrap gap-md overflow-auto hide-scrollbar" }
const _hoisted_6 = {
  key: 0,
  class: "column no-wrap gap-md"
}
const _hoisted_7 = { class: "fs-13px fw-500 text-space-b-100" }
const _hoisted_8 = { class: "q-mr-xs" }
const _hoisted_9 = { class: "row items-center justify-between no-wrap gap-md" }

import _ from 'lodash'
import { computed, Ref, ref, toValue } from 'vue'
import LandingListFilterItem from './LandingListFilterItem.vue'
import { IFilter, IFilterModel } from 'src/common/interfaces'
import translate from 'src/common/helpers/language/translate'
import globalTranslateObject from 'src/translate/global.translate'

interface IProps {
  filters?: IFilter[]
  filterModel: IFilterModel
}

type Emits = { apply: [filter: IFilterData] }

export interface IFilterObj {
  label: string
  value: unknown
  key: string
}

export interface IFilterData {
  applied: IFilterObj[]
  model: IFilterModel
  [key: string]: unknown
}


export default /*@__PURE__*/_defineComponent({
  ...{ name: 'LandingListFilters' },
  __name: 'LandingListFilters',
  props: /*@__PURE__*/_mergeModels({
    filters: { default: () => [] },
    filterModel: { default: () => ({}) }
  }, {
    "modelValue": { type: Boolean, ...{ default: false } },
    "modelModifiers": {},
  }),
  emits: /*@__PURE__*/_mergeModels(["apply"], ["update:modelValue"]),
  setup(__props: any, { expose: __expose, emit: __emit }) {


const props = __props
const emit = __emit
const dialogModel = _useModel<boolean>(__props, "modelValue")

const { t: globalT } = translate(globalTranslateObject)

const filterRef: Ref<IFilterModel> = ref(toValue(() => props.filterModel))
const filtersRef: Ref<IFilter[]> = ref(toValue(() => props.filters))
const flatFilters = _.flatMap(filtersRef.value, (filter) => {
  if (filter.type === 'group' && filter.children) {
    const flatChildren = _.map(filter.children, (child) => {
      return { ...child, parentGroup: filter.name }
    })
    return [filter, ...flatChildren]
  } else {
    return filter
  }
})

// Snapshot of the last state actually emitted to the parent. Used to discard
// any in-popup edits that never got confirmed via the Apply button.
let committedModel: IFilterModel = _.cloneDeep(filterRef.value)

const initialModel = { ...filterRef.value }

for (const key of Object.keys(initialModel)) {
  if (typeof initialModel[key] === 'string') initialModel[key] = ''
  else if (typeof initialModel[key] === 'boolean') initialModel[key] = false
  else if (initialModel[key] instanceof Array) initialModel[key] = []
  else initialModel[key] = undefined
}

function applyFilters() {
  if (_.isEqual(filterRef.value, committedModel)) return
  const appliedFilters = getAppliedFiltersList(filterRef.value, initialModel)
  committedModel = _.cloneDeep(filterRef.value)
  emit('apply', { model: filterRef.value, applied: appliedFilters })
}

// Bound to the popup's own Apply button: commits the draft (only emitting if
// it actually changed) and always closes the popup.
function confirmFilters() {
  applyFilters()
  dialogModel.value = false
}

function resetFieldsToDefault() {
  for (const filter of flatFilters) {
    const key = filter?.field
    if (key) {
      if (!filter?.default) filterRef.value[key] = initialModel[key]
      else filterRef.value[key] = filter?.default
    }
  }
}

// Exposed for callers outside the popup (e.g. the "clear all" icon in the
// applied-filters bar) where there is no Apply button, so the reset must
// take effect immediately.
function resetFilters() {
  resetFieldsToDefault()
  applyFilters()
}

// Bound to the popup's own "Clear All" button: resets, commits immediately
// (like an implicit Apply), and closes the popup.
function clearFilters() {
  resetFilters()
  dialogModel.value = false
}

function resetFilterByKey(key: string) {
  const filter = flatFilters.find((filter) => filter.field === key)
  if (!filter?.default) filterRef.value[key] = initialModel[key]
  else filterRef.value[key] = filter?.default
  applyFilters()
}

// Bound to the popup's @hide: if the popup closes without the user clicking
// Apply, any draft edits are discarded.
function discardUnappliedChanges() {
  if (!_.isEqual(filterRef.value, committedModel)) filterRef.value = _.cloneDeep(committedModel)
}

function getAppliedFiltersList(filterValues: IFilterModel, initialValues: { [key: string]: unknown }) {
  const appliedFilters = []
  for (const key of Object.keys(filterValues)) {
    if (!_.isEqual(filterValues[key], initialValues[key])) {
      const filter = flatFilters.find((filterObj) => filterObj?.field === key)

      if (filter) {
        const appliedFilterObj = {
          key,
          value: filterValues[key],
          label: '',
        }
        if (filter.type === 'dropdown' || filter.type === 'checkbox-group') {
          appliedFilterObj.label = filter.tag ?? ''
          if (filterValues[key] instanceof Array) {
            const selectedOptions = []
            for (const id of filterValues[key]) {
              const dropdownOption = filter.bind?.options?.find((option) => option.value === id)
              if (dropdownOption) selectedOptions.push(dropdownOption.label)
            }
            appliedFilterObj.value = selectedOptions.join(', ')
          } else {
            const selectedOption = filter.bind?.options?.find((option) => option.value === filterValues[key])
            if (selectedOption) appliedFilterObj.value = selectedOption.label
          }
        } else {
          appliedFilterObj.label = filter.tag ?? ''
        }
        appliedFilters.push(appliedFilterObj)
      }
    }
  }
  return appliedFilters
}

const appliedFiltersCount = computed(() => {
  return getAppliedFiltersList(filterRef.value, initialModel).length
})

// watch(
//   () => props.filters,
//   () => {
//     for (const filter of props.filters) {
//       if (filter?.field?.includes(',')) {
//         const keys = filter?.field?.split(',')
//         initialModel[keys[0].trim()] = filter?.default ?? ''
//         initialModel[keys[1].trim()] = filter?.default ?? ''
//       } else {
//         const key = filter?.field
//         if (!filter?.default) {
//           if (typeof initialModel[key] === 'string') initialModel[key] = ''
//           else if (typeof initialModel[key] === 'boolean')
//             initialModel[key] = false
//           else if (initialModel[key] instanceof Array) initialModel[key] = []
//           else initialModel[key] = null
//         } else initialModel[key] = filter?.default
//       }
//     }
//     console.log(initialModel)
//   },
//   { deep: true }
// )

__expose({ resetFilters, resetFilterByKey })

return (_ctx: any,_cache: any) => {
  const _component_k_icon = _resolveComponent("k-icon")!
  const _component_k_btn = _resolveComponent("k-btn")!
  const _component_k_divider = _resolveComponent("k-divider")!
  const _component_q_popup_proxy = _resolveComponent("q-popup-proxy")!

  return (_openBlock(), _createBlock(_component_q_popup_proxy, {
    modelValue: dialogModel.value,
    "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((dialogModel).value = $event)),
    class: "LandingListFilters overflow-hidden",
    "no-parent-event": "",
    offset: [0, 8],
    "transition-show": "jump-down",
    "transition-hide": "jump-up",
    onHide: discardUnappliedChanges
  }, {
    default: _withCtx(() => [
      _createElementVNode("div", _hoisted_1, [
        _createElementVNode("div", _hoisted_2, [
          _createElementVNode("div", _hoisted_3, [
            _createVNode(_component_k_icon, {
              name: "filters",
              size: "24px",
              color: "cool-gray-70"
            }),
            _createElementVNode("span", _hoisted_4, _toDisplayString(_unref(globalT)('filter_by')), 1)
          ]),
          _createVNode(_component_k_btn, {
            model: "link",
            icon: "close-rounded",
            "icon-size": "24px",
            "custom-color": "cool-gray-70",
            onClick: _cache[0] || (_cache[0] = ($event: any) => (dialogModel.value = false))
          })
        ]),
        _createVNode(_component_k_divider, {
          size: "md",
          class: "q-mt-20 q-mb-20"
        }),
        _createElementVNode("div", _hoisted_5, [
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(__props.filters, (filterObj, index) => {
            return (_openBlock(), _createElementBlock(_Fragment, { key: index }, [
              (index > 0)
                ? (_openBlock(), _createBlock(_component_k_divider, {
                    key: 0,
                    dotted: true,
                    color: "cool-gray-50"
                  }))
                : _createCommentVNode("", true),
              _createVNode(LandingListFilterItem, {
                modelValue: filterRef.value,
                "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((filterRef).value = $event)),
                filter: filterObj
              }, null, 8, ["modelValue", "filter"])
            ], 64))
          }), 128)),
          _renderSlot(_ctx.$slots, "filters")
        ]),
        (appliedFiltersCount.value > 0)
          ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
              _createVNode(_component_k_divider, {
                dotted: true,
                color: "cool-gray-50",
                class: "q-mt-md"
              }),
              _createElementVNode("span", _hoisted_7, [
                _createElementVNode("span", _hoisted_8, _toDisplayString(appliedFiltersCount.value), 1),
                _createElementVNode("span", null, _toDisplayString(_unref(globalT)('filters_applied')), 1)
              ])
            ]))
          : _createCommentVNode("", true),
        _createVNode(_component_k_divider, {
          size: "md",
          class: "q-mt-20 q-mb-20"
        }),
        _createElementVNode("div", _hoisted_9, [
          _createVNode(_component_k_btn, {
            model: "secondary",
            size: "md",
            icon: "trash",
            "is-right-icon": "",
            color: "error-100",
            label: _unref(globalT)('clear_all'),
            class: "flex-1",
            onClick: clearFilters
          }, null, 8, ["label"]),
          _createVNode(_component_k_btn, {
            model: "primary",
            size: "md",
            label: _unref(globalT)('apply'),
            class: "LandingListFilters__apply-btn flex-1",
            onClick: confirmFilters
          }, null, 8, ["label"])
        ])
      ])
    ]),
    _: 3
  }, 8, ["modelValue"]))
}
}

})